/**
 * Validates time periods for opening hours
 * @param {Array} periods - Array of period objects with openingTime and closingTime
 * @returns {Object} - Object containing isValid (boolean) and errors (array of error messages)
 */
export const validateTimePeriods = (periods) => {
  const errors = [];
  const periodMap = new Map(); // To track duplicate periods
  const sortedPeriods = [...periods].sort((a, b) => {
    const compareOpening = a.openingTime.localeCompare(b.openingTime);
    if (compareOpening !== 0) {
      return compareOpening;
    }
    return a.closingTime.localeCompare(b.closingTime);
  });

  // Check for duplicate periods
  for (const [index, period] of sortedPeriods.entries()) {
    const periodKey = `${period.openingTime}-${period.closingTime}`;

    if (periodMap.has(periodKey)) {
      errors.push({
        type: 'DUPLICATE_PERIOD',
        message: `Duplicate time period found: ${period.openingTime} - ${period.closingTime}`,
        periodIndex: index,
      });
    } else {
      periodMap.set(periodKey, true);
    }
  }

  // Check for invalid time ranges and overlapping periods
  for (let i = 0; i < sortedPeriods.length; i++) {
    const current = sortedPeriods[i];

    // Check if closing time is after opening time
    // Allow cross-midnight hours (closingTime < openingTime indicates overnight hours)
    const isCrossMidnight = current.openingTime > current.closingTime;

    if (!isCrossMidnight && current.openingTime >= current.closingTime) {
      errors.push({
        type: 'INVALID_TIME_RANGE',
        message: `Closing time must be after opening time: ${current.openingTime} - ${current.closingTime}`,
        periodIndex: i,
      });
    }

    // Check for overlapping with next period
    if (i < sortedPeriods.length - 1) {
      const next = sortedPeriods[i + 1];

      // Handle overlapping checks based on whether periods are cross-midnight
      let hasOverlap = false;

      if (isCrossMidnight) {
        // For cross-midnight hours, the period spans to the next day
        // Check if next period starts before current period ends (on the next calendar day)
        // A cross-midnight period (e.g., 18:00-02:00) is "active" from 18:00 to 02:00+24h = 26:00
        // So we compare: next.openingTime < current.closingTime (with the +24h adjustment)
        const currentEndInMinutes = timeToMinutes(current.closingTime) + 1440; // Add 24 hours
        const nextStartInMinutes = timeToMinutes(next.openingTime);
        hasOverlap = nextStartInMinutes < currentEndInMinutes;
      } else {
        // Normal period - check if next starts before current ends
        hasOverlap = current.closingTime > next.openingTime;
      }

      if (hasOverlap) {
        errors.push({
          type: 'OVERLAPPING_PERIODS',
          message: `Time period overlaps with next period: ${current.openingTime} - ${current.closingTime} and ${next.openingTime} - ${next.closingTime}`,
          periodIndex: i,
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Convert time string (HH:MM) to minutes since midnight
 * @param {string} time - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
const timeToMinutes = (time) => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Validates all days' opening hours
 * @param {Array} schedule - Array of day objects with periods
 * @returns {Object} - Object containing isValid (boolean) and errors (nested object by day index)
 */
export const validateSchedule = (schedule) => {
  if (!schedule || !Array.isArray(schedule)) {
    return {
      isValid: false,
      errors: { _global: 'Invalid schedule format' },
    };
  }

  const errors = {};
  let isValid = true;
  const dateMap = new Map();

  schedule.forEach((day, dayIndex) => {
    // Check if date is required for temporary closures (items with date property)
    if (day.date !== undefined && (!day.date || day.date.trim() === '')) {
      isValid = false;
      if (!errors[dayIndex]) {
        errors[dayIndex] = { _errors: [] };
      }
      errors[dayIndex]._errors.push({
        type: 'MISSING_DATE',
        message: 'Date is required for holiday hours',
      });
    }

    if (day.date && day.date.trim() !== '') {
      if (dateMap.has(day.date)) {
        isValid = false;
        const firstIndex = dateMap.get(day.date);
        if (!errors[firstIndex]) {
          errors[firstIndex] = { _errors: [] };
        }
        errors[firstIndex]._errors.push({
          type: 'DUPLICATE_DATE',
          message: `Duplicate date found: ${day.date}`,
        });
        if (!errors[dayIndex]) {
          errors[dayIndex] = { _errors: [] };
        }
        errors[dayIndex]._errors.push({
          type: 'DUPLICATE_DATE',
          message: `Duplicate date found: ${day.date}`,
        });
      } else {
        dateMap.set(day.date, dayIndex);
      }
    }
  });

  schedule.forEach((day, dayIndex) => {
    if (!day.isOpen || !day.periods || day.periods.length === 0) {
      return; // Skip validation for closed days or days with no periods
    }

    const { isValid: dayIsValid, errors: dayErrors } = validateTimePeriods(
      day.periods,
    );

    if (!dayIsValid) {
      isValid = false;
      if (!errors[dayIndex]) {
        errors[dayIndex] = {};
      }
      if (!errors[dayIndex].periods) {
        errors[dayIndex].periods = day.periods.map((_, periodIndex) => ({
          openingTime: null,
          closingTime: null,
          _errors: [],
        }));
      }

      dayErrors.forEach((error) => {
        const periodIndex = error.periodIndex;
        if (!errors[dayIndex].periods[periodIndex]) {
          errors[dayIndex].periods[periodIndex] = {
            openingTime: null,
            closingTime: null,
            _errors: [],
          };
        }

        errors[dayIndex].periods[periodIndex]._errors.push({
          type: error.type,
          message: error.message,
        });
      });
    }
  });

  return {
    isValid,
    errors: Object.keys(errors).length > 0 ? { weeklySchedule: errors } : {},
  };
};

/**
 * Helper function to format time in 12-hour format for display
 */
export const formatTimeForDisplay = (time24) => {
  if (!time24) return '';

  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;

  return `${hour12}:${minutes} ${ampm}`;
};

/**
 * Check if a time period spans past midnight (cross-midnight)
 * @param {string} openingTime - Opening time in HH:MM format
 * @param {string} closingTime - Closing time in HH:MM format
 * @returns {boolean} - True if the period spans past midnight
 */
export const isCrossMidnightPeriod = (openingTime, closingTime) => {
  if (!openingTime || !closingTime) return false;
  return openingTime > closingTime;
};

/**
 * Format a time period for display, handling cross-midnight display
 * @param {string} openingTime - Opening time in HH:MM format
 * @param {string} closingTime - Closing time in HH:MM format
 * @returns {string} - Formatted time period string (e.g., "6:00 PM - 2:00 AM")
 */
export const formatTimePeriodForDisplay = (openingTime, closingTime) => {
  if (!openingTime || !closingTime) return '';

  const formattedOpen = formatTimeForDisplay(openingTime);
  const formattedClose = formatTimeForDisplay(closingTime);

  return `${formattedOpen} - ${formattedClose}`;
};
